import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { showToast } from "src/app/methods/showToast";
import { Metadata } from "src/app/Task/Subtask/Subtask";
import { DictionaryList } from "src/redux/dictionary/types";
import { RootState } from "src/redux/reducers";
import { DictionariesService } from "src/services";
import { SkeletonLoader } from "../GlobalActivityTab/utils";
import AssignDictionaryModal from "./components/AssignDictionaryModal/AssignDictionaryModal";
import DictionaryTabList from "./components/DictionaryTabList/DictionaryTabList";
import EmptyDictionaryTab from "./components/EmptyDictionaryTab/EmptyDictionaryTab";
import GlobalTaskAddNewDictionaryElementModal from "../../components/GlobalTaskAddNewDictionaryElementModal/GlobalTaskAddNewDictionaryElementModal";

export interface DictionaryItem {
  wsDictionaryElementName: string;
  wsDictionaryGlobalCreatorMetaFieldUuid: string;
  wsDictionaryName: string;
  wsDictionaryUrl: string;
  wsDictionaryIcon: string;
  wsDictionaryUuid: string;
}

export interface DictionaryTabElement {
  metadata: Metadata;
  title: string;
  titleMetaFieldUuid: string;
  uuid: string;
}

interface Props {
  activeTab: string;
}

export default function DictionaryTab({ activeTab }: Props) {
  const globalTaskDetails = useSelector(
    (state: RootState) => state.creatorDatabaseReducer.globalTaskDetails,
  );

  const [assignedDictionaryElements, setAssignedDictionaryElements] =
    useState<DictionaryList | null>(null);
  const [allDictionaryElements, setAllDictionaryElements] =
    useState<DictionaryList | null>(null);
  const [isDictionaryListLoading, setIsDictionaryListLoading] = useState(false);
  const [showAssignDictionaryModal, setShowAssignDictionaryElementModal] =
    useState(false);
  const [
    showAddNewDictionaryElementModal,
    setShowAddNewDictionaryElementModal,
  ] = useState(false);

  const dictionaryData = useMemo(
    () =>
      globalTaskDetails?.wsDictionariesRelated.find(
        (item) => item.wsDictionaryUrl === activeTab,
      ),
    [globalTaskDetails, activeTab],
  );

  const getDictionaryList = async (
    wsDictionaryUuid: string,
    updateFunction: Dispatch<SetStateAction<DictionaryList | null>>,
    filters?: { [key: string]: { [key: string]: string[] } },
  ) => {
    const response = await DictionariesService.getList(wsDictionaryUuid, {
      params: { filters },
    });
    updateFunction(response);
  };

  const getLists = useCallback(async () => {
    if (
      !dictionaryData?.wsDictionaryUuid ||
      !dictionaryData?.wsDictionaryGlobalCreatorMetaFieldUuid
    )
      return;

    setIsDictionaryListLoading(true);
    try {
      await getDictionaryList(
        dictionaryData.wsDictionaryUuid,
        setAssignedDictionaryElements,
        {
          [dictionaryData.wsDictionaryGlobalCreatorMetaFieldUuid]: {
            value: [globalTaskDetails.uuid],
          },
        },
      );
      await getDictionaryList(
        dictionaryData.wsDictionaryUuid,
        setAllDictionaryElements,
      );
    } catch (error) {
      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id="ws_something_went_wrong"
          defaultMessage="Something went wrong"
        />,
      );
    } finally {
      setIsDictionaryListLoading(false);
    }
  }, [
    dictionaryData?.wsDictionaryUuid,
    dictionaryData?.wsDictionaryGlobalCreatorMetaFieldUuid,
    globalTaskDetails?.uuid,
  ]);

  useEffect(() => {
    getLists();
  }, [getLists]);

  const renderContent = () => {
    if (isDictionaryListLoading) {
      return <SkeletonLoader avatarShape="rect" />;
    }

    if (!assignedDictionaryElements?.list.length) {
      return (
        <EmptyDictionaryTab
          showAssignDictionaryElementModal={() =>
            setShowAssignDictionaryElementModal(true)
          }
          showAddNewDictionaryElementModal={() =>
            setShowAddNewDictionaryElementModal(true)
          }
          dictionaryData={dictionaryData}
        />
      );
    }

    return (
      <DictionaryTabList
        list={assignedDictionaryElements.list}
        dictionaryData={dictionaryData}
        showAssignDictionaryElementModal={() =>
          setShowAssignDictionaryElementModal(true)
        }
        showAddNewDictionaryElementModal={() =>
          setShowAddNewDictionaryElementModal(true)
        }
        isDictionaryListLoading={isDictionaryListLoading}
      />
    );
  };

  return (
    <div className="dictionary-tab">
      {renderContent()}

      {showAssignDictionaryModal && dictionaryData && (
        <AssignDictionaryModal
          onClose={() => setShowAssignDictionaryElementModal(false)}
          dictionaryData={dictionaryData}
          assignedDictionaryElements={assignedDictionaryElements}
          allDictionaryElements={allDictionaryElements}
          getLists={getLists}
        />
      )}

      {showAddNewDictionaryElementModal && dictionaryData && (
        <GlobalTaskAddNewDictionaryElementModal
          onClose={() => setShowAddNewDictionaryElementModal(false)}
          dictionaryUuid={dictionaryData?.wsDictionaryUuid}
          getLists={getLists}
          globalTaskUuid={globalTaskDetails.uuid}
        />
      )}
    </div>
  );
}
